import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Users, ArrowLeft, ExternalLink, Server, ChevronDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { subnetNames } from '../utils/subnetNames';
import { subnetDescriptions } from '../utils/subnetDescriptions';
import { UserEngagementChart } from '../components/UserEngagementChart';
import { logDocumentFields } from '../utils/debugUtils';

interface Tweet {
  id: string;
  text: string;
  created_at: string;
  engagement_score: number;
  link: string;
  subnet_matches: string[];
  username?: string;
  profile_image_url?: string;
}

interface UserStats {
  username: string;
  engagement_score: number;
  percent_change_24h: number;
  profile_image: string;
}

interface SubnetMention {
  name: string;
  netuid: number;
  mentionCount: number;
  profileImageUrl?: string;
  description?: string;
}

export default function KOLProfilePage() {
  const { username } = useParams<{ username: string }>();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [tweets, setTweets] = useState<Tweet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subnetMentions, setSubnetMentions] = useState<SubnetMention[]>([]);
  const [expandedTweets, setExpandedTweets] = useState<Record<string, boolean>>({});
  const [sevenDayEngagement, setSevenDayEngagement] = useState<number | null>(null);
  
  // Process tweets to extract subnet mentions
  useEffect(() => {
    if (tweets.length === 0) return;
    
    const mentionsBySubnet: Record<string, { count: number, netuid?: number }> = {};
    
    tweets.forEach(tweet => {
      const tweetText = tweet.text.toLowerCase();
      
      // Process subnet_matches from the tweet
      if (tweet.subnet_matches && tweet.subnet_matches.length > 0) {
        tweet.subnet_matches.forEach(subnet => {
          if (!mentionsBySubnet[subnet]) {
            mentionsBySubnet[subnet] = { count: 0 };
          }
          mentionsBySubnet[subnet].count += 1;
        });
      }
      
      // Special case for StreetVision (SN72)
      if (tweetText.includes('streetvision') || 
          tweetText.includes('street vision') || 
          tweetText.includes('natix')) {
        if (!mentionsBySubnet['StreetVision By NATIX']) {
          mentionsBySubnet['StreetVision By NATIX'] = { count: 0, netuid: 72 };
        }
        mentionsBySubnet['StreetVision By NATIX'].count += 1;
      }
    });
    
    // Find netuid for each subnet name
    Object.entries(mentionsBySubnet).forEach(([name, data]) => {
      if (data.netuid === undefined) {
        // Skip Root subnet
        if (name.toLowerCase() === 'root') return;
        
        const matchingNetuid = Object.entries(subnetNames).find(([_, subnetName]) => 
          subnetName.toLowerCase() === name.toLowerCase()
        );
        
        if (matchingNetuid) {
          mentionsBySubnet[name].netuid = parseInt(matchingNetuid[0]);
        }
      }
    });
    
    // Convert to array and sort by count
    const subnetArray: SubnetMention[] = Object.entries(mentionsBySubnet)
      .map(([name, data]) => ({
        name,
        netuid: data.netuid || 0,
        mentionCount: data.count,
        description: data.netuid ? subnetDescriptions[data.netuid]?.description : undefined,
        profileImageUrl: getSubnetProfileImageUrl(data.netuid)
      }))
      .filter(item => {
        // Only include subnets with valid netuid and 2+ mentions
        // Explicitly exclude Root subnet (0)
        return item.netuid > 0 && item.mentionCount >= 2;
      })
      .sort((a, b) => b.mentionCount - a.mentionCount);
    
    setSubnetMentions(subnetArray);
  }, [tweets]);

  // Get profile image URL for a subnet
  const getSubnetProfileImageUrl = (netuid?: number): string | undefined => {
    if (!netuid) return undefined;
    
    // Special case for subnet 14
    if (netuid === 14) {
      return "https://pbs.twimg.com/profile_images/1922656948530577409/mLnpdbFj_400x400.jpg";
    }
    
    // Special case for StreetVision (SN72)
    if (netuid === 72) {
      return "https://pbs.twimg.com/profile_images/1649081080692695046/NATIXNetwork_400x400.jpg";
    }
    
    const subnetInfo = subnetDescriptions[netuid];
    if (!subnetInfo?.x) return undefined;
    
    // Extract username from X/Twitter URL
    const xUrlMatch = subnetInfo.x.match(/twitter\.com\/(\w+)|x\.com\/(\w+)/i);
    if (!xUrlMatch) return undefined;
    
    const xUsername = (xUrlMatch[1] || xUrlMatch[2] || '').toLowerCase();
    return `https://unavatar.io/twitter/${xUsername}`;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (!username) return;
      
      try {
        setLoading(true);
        
        // Debug: Log the user stats document
        if (username === 'Old_Samster') {
          await logDocumentFields('user_stats', username);
        }
        
        // Fetch user stats
        const userStatsRef = collection(db, 'user_stats');
        const userStatsQuery = query(userStatsRef, where('username', '==', username.toLowerCase()));
        const userStatsSnapshot = await getDocs(userStatsQuery);
        
        if (userStatsSnapshot.empty) {
          // Try to get by document ID
          const userStatDoc = await getDocs(collection(db, 'user_stats'));
          const matchingDoc = userStatDoc.docs.find(doc => doc.id.toLowerCase() === username.toLowerCase());
          
          if (matchingDoc) {
            setUserStats({
              username: matchingDoc.id,
              ...matchingDoc.data() as Omit<UserStats, 'username'>
            });
          } else {
            setError(`User ${username} not found`);
            setLoading(false);
            return;
          }
        } else {
          setUserStats({
            username: username,
            ...userStatsSnapshot.docs[0].data() as Omit<UserStats, 'username'>
          });
        }
        
        // Fetch 7-day engagement percentage - get the most recent document
        const engagementCollectionRef = collection(db, 'user_engagement_history', username, '7_day_engagement');
        const engagementQuery = query(engagementCollectionRef, orderBy('timestamp', 'desc'), limit(1));
        const engagementSnapshot = await getDocs(engagementQuery);
        
        if (!engagementSnapshot.empty) {
          const mostRecentDoc = engagementSnapshot.docs[0];
          const data = mostRecentDoc.data();
          setSevenDayEngagement(data.engagement_share_percentage || null);
          console.log(`Found most recent 7-day engagement for ${username} from ${mostRecentDoc.id}:`, data.engagement_share_percentage);
        } else {
          console.log(`No 7-day engagement data found for ${username}`);
        }
        
        // Fetch user tweets - try both username and lowercase username
        let tweetsSnapshot;
        
        // First try with the exact username
        const tweetsRef = collection(db, 'users', username, 'tweets');
        const tweetsQuery = query(
          tweetsRef,
          orderBy('engagement_score', 'desc'),
          limit(50)
        );
        
        tweetsSnapshot = await getDocs(tweetsQuery);
        
        // If no tweets found, try with lowercase username
        if (tweetsSnapshot.empty) {
          console.log(`No tweets found for user ${username}, trying lowercase`);
          const lowercaseTweetsRef = collection(db, 'users', username.toLowerCase(), 'tweets');
          const lowercaseTweetsQuery = query(
            lowercaseTweetsRef,
            orderBy('engagement_score', 'desc'),
            limit(50)
          );
          
          tweetsSnapshot = await getDocs(lowercaseTweetsQuery);
        }
        
        // If still no tweets, try with the username from user_stats
        if (tweetsSnapshot.empty && userStats) {
          console.log(`No tweets found for user ${username.toLowerCase()}, trying with user_stats username`);
          const statsUsername = userStats.username.toLowerCase();
          const statsTweetsRef = collection(db, 'users', statsUsername, 'tweets');
          const statsTweetsQuery = query(
            statsTweetsRef,
            orderBy('engagement_score', 'desc'),
            limit(50)
          );
          
          tweetsSnapshot = await getDocs(statsTweetsQuery);
        }
        
        // If still no tweets, try with the all_tweets collection
        if (tweetsSnapshot.empty) {
          console.log(`No tweets found in user collection, trying all_tweets`);
          const allTweetsRef = collection(db, 'all_tweets');
          const allTweetsQuery = query(
            allTweetsRef,
            where('username', '==', username.toLowerCase()),
            orderBy('engagement_score', 'desc'),
            limit(50)
          );
          
          tweetsSnapshot = await getDocs(allTweetsQuery);
        }
        
        if (tweetsSnapshot.empty) {
          console.log(`No tweets found for user ${username}`);
        } else {
          const tweetData = tweetsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            username: username
          } as Tweet));
          
          setTweets(tweetData);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load user data');
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [username, userStats?.username]);

  const toggleTweetExpansion = (tweetId: string) => {
    setExpandedTweets(prev => ({
      ...prev,
      [tweetId]: !prev[tweetId]
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
          <div className="animate-pulse space-y-4">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-black/50 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-6 bg-black/50 rounded w-48"></div>
                <div className="h-4 bg-black/50 rounded w-32"></div>
              </div>
            </div>
            <div className="h-40 bg-black/50 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !userStats) {
    return (
      <div className="space-y-6">
        <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
          <div className="flex items-center space-x-3 mb-6">
            <Users className="w-8 h-8 text-cyan-400" />
            <h1 className="text-2xl md:text-3xl font-bold">KOL Profile</h1>
          </div>
          <div className="bg-red-900/30 text-red-300 p-4 rounded-lg">
            {error || `User ${username} not found`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Users className="w-8 h-8 text-cyan-400" />
            <h1 className="text-2xl md:text-3xl font-bold">KOL Profile</h1>
          </div>
          <Link 
            to="/kolscan" 
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to KOLScan</span>
          </Link>
        </div>

        {/* User Profile Header */}
        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/5 mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="relative">
              <img 
                src={userStats.profile_image || "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png"} 
                alt={`${userStats.username}'s profile`}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full border-2 border-cyan-400 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png";
                }}
              />
              <div className="absolute -bottom-2 -right-2 bg-black/80 rounded-full p-1.5">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="w-5 h-5 text-cyan-400"
                >
                  <path d="M4 4l11.733 16h4.267l-11.733 -16z"/>
                  <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/>
                </svg>
              </div>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold">@{userStats.username}</h2>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/5">
                  <div className="text-sm text-gray-400">Engagement Score</div>
                  <div className="text-xl font-bold">{userStats.engagement_score.toLocaleString()}</div>
                  <div className={`text-sm ${userStats.percent_change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {userStats.percent_change_24h >= 0 ? '↑' : '↓'} {Math.abs(userStats.percent_change_24h).toFixed(2)}% (24h)
                  </div>
                </div>
                
                <div className="bg-black/30 backdrop-blur-sm rounded-lg p-3 border border-white/5">
                  <div className="text-sm text-gray-400">7-Day Engagement Share</div>
                  <div className="text-xl font-bold">
                    {sevenDayEngagement !== null ? `${sevenDayEngagement.toFixed(2)}%` : 'N/A'}
                  </div>
                  <div className="text-sm text-gray-400">
                    of total engagement
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section - Subnet Mentions and Recent Activity */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Subnet Mentions - 65% width */}
          <div className="w-full md:w-[65%]">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Server className="w-5 h-5 text-cyan-400 mr-2" />
              Subnet Mentions
            </h2>
            
            {subnetMentions.length > 0 ? (
              <div className="flex flex-wrap gap-8">
                {subnetMentions.map((subnet) => (
                  <Link 
                    key={subnet.netuid}
                    to={`/subnet/${subnet.netuid}`}
                    className="flex flex-col items-center"
                  >
                    {subnet.profileImageUrl ? (
                      <div className="w-16 h-16 rounded-full overflow-hidden mb-2">
                        <img 
                          src={subnet.profileImageUrl}
                          alt={subnet.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-black/30 flex items-center justify-center mb-2">
                        <Server className="w-8 h-8 text-cyan-400" />
                      </div>
                    )}
                    <h3 className="font-semibold text-center">{subnet.name}</h3>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5 text-center text-gray-400">
                No subnet mentions found with 2+ mentions
              </div>
            )}
            
            {/* User Engagement Chart */}
            <div className="mt-8">
              <UserEngagementChart username={username} />
            </div>
          </div>

          {/* Recent Activity - 35% width */}
          <div className="w-full md:w-[35%]">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="w-5 h-5 text-cyan-400 mr-2"
              >
                <path d="M4 4l11.733 16h4.267l-11.733 -16z"/>
                <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/>
              </svg>
              Recent Activity
            </h2>
            
            {tweets.length > 0 ? (
              <div className="space-y-4">
                {tweets
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .slice(0, 5)
                  .map(tweet => (
                  <div key={tweet.id} className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5">
                    <div className="flex items-center">
                      <img 
                        src={tweet.profile_image_url || userStats.profile_image || "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png"}
                        alt="Profile"
                        className="w-10 h-10 rounded-full flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png";
                        }}
                      />
                      <div className="ml-3 flex items-center">
                        <span className="font-bold text-sm">@{tweet.username || userStats.username}</span>
                        <span className="text-gray-400 text-xs ml-2">
                          {formatDistanceToNow(new Date(tweet.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-2">
                      <p className={`text-sm ${expandedTweets[tweet.id] ? '' : 'line-clamp-3'}`}>
                        {tweet.text}
                      </p>
                      
                      {tweet.text.length > 150 && (
                        <button 
                          onClick={() => toggleTweetExpansion(tweet.id)}
                          className="text-cyan-400 hover:text-cyan-300 text-xs mt-1"
                        >
                          {expandedTweets[tweet.id] ? 'Show Less' : 'Show More'}
                        </button>
                      )}
                    </div>
                    
                    <div className="flex justify-end mt-2">
                      <a 
                        href={tweet.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-black/20 backdrop-blur-sm rounded-lg p-4 border border-white/5 text-center text-gray-400">
                No tweets found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}